import React, { useState, useEffect, useCallback } from 'react';
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import { absURL } from '../utils';

export default function DeezerSearch({ onTrackSelected, selectedTrack, label = "Sök låt på Deezer:" }) {

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [previewAudio, setPreviewAudio] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearchError("");
    setResults([]);
    try {
      const response = await fetch(absURL(`/api/deezer/search?q=${encodeURIComponent(query)}`));
      const data = await response.json();
      if (data.success && data.response.tracks) {
        setResults(data.response.tracks);
      } else {
        setSearchError("Inga resultat hittades.");
      }
    } catch (e) {
      setSearchError("Kunde inte ansluta till söktjänsten.");
    }
    setSearching(false);
  }, [query]);

  const handleKeyDown = (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      handleSearch();
    }
  };

  const handlePreview = useCallback((track) => {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
    }
    if (playingId === track.id) {
      setPlayingId(null);
      setPreviewAudio(null);
      return;
    }
    const audio = new Audio(track.previewUrl);
    audio.play();
    audio.onended = () => {
      setPlayingId(null);
      setPreviewAudio(null);
    };
    setPreviewAudio(audio);
    setPlayingId(track.id);
  }, [previewAudio, playingId]);

  useEffect(() => {
    return () => {
      if (previewAudio) previewAudio.pause();
    };
  }, [previewAudio]);

  return (
    <div className="form-group mt-3">
      <label>{label}</label>

      {selectedTrack && (
        <div className="d-flex align-items-center p-2 mb-2 border rounded" style={{ background: "#f8f9fa", gap: "12px" }}>
          <img src={selectedTrack.cover} alt="" style={{ width: "48px", height: "48px", borderRadius: "4px" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>{selectedTrack.title}</div>
            <div style={{ fontSize: "13px", color: "#666" }}>{selectedTrack.artist} — {selectedTrack.album}</div>
          </div>
          <span style={{ fontSize: "11px", color: "#28a745", fontWeight: 500 }}>✓ Vald</span>
        </div>
      )}

      <div className="d-flex" style={{ gap: "8px" }}>
        <input
          type="text"
          className="form-control"
          placeholder="Sök på artist, låt eller album..."
          value={query}
          onChange={ev => setQuery(ev.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          type="button"
          variant="outline-secondary"
          onClick={handleSearch}
          disabled={searching || !query.trim()}>
          {searching ? <Spinner animation="border" size="sm" /> : "Sök"}
        </Button>
      </div>

      {searchError && <Alert variant="warning" className="mt-2">{searchError}</Alert>}

      {results.length > 0 && (
        <div className="mt-2 border rounded" style={{ maxHeight: "320px", overflowY: "auto" }}>
          {results.map(track => (
            <div
              key={track.id}
              className="d-flex align-items-center p-2"
              style={{
                borderBottom: "1px solid #eee",
                background: selectedTrack && selectedTrack.id === track.id ? "#e8f4fd" : "white",
                gap: "10px"
              }}>

              <img src={track.cover} alt="" style={{ width: "40px", height: "40px", borderRadius: "3px", flexShrink: 0 }} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {track.title}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {track.artist} — {track.album}
                </div>
              </div>

              <div className="d-flex" style={{ gap: "6px", flexShrink: 0 }}>
                <Button
                  type="button"
                  size="sm"
                  variant={playingId === track.id ? "secondary" : "outline-secondary"}
                  onClick={() => handlePreview(track)}
                  title="Förhandslyssna (30 sek)">
                  {playingId === track.id ? "■" : "▶"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={selectedTrack && selectedTrack.id === track.id ? "success" : "outline-primary"}
                  onClick={() => onTrackSelected(track)}>
                  {selectedTrack && selectedTrack.id === track.id ? "Vald ✓" : "Välj"}
                </Button>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}
